import React from 'react';
import TableRow from '@mui/material/TableRow';
import Stack from '@mui/material/Stack';
import styled from 'styled-components';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import { Button } from 'ff/debrief/common/components/StyledComponents/Buttons';
import { ForeFlightTextField } from 'ff/debrief/common/components/StyledComponents/TextFields';
import { FFTheme } from 'ff/common/theme/StyledComponentThemes';

import { useField } from 'formik';
import { VspeedValues } from 'ff/debrief/components/modals/VspeedModal/VspeedFormValues';
import { convertSpeed, KTS_TO_MPH } from 'ff/debrief/utils';

const SpeedRow = (props: {
    name: keyof VspeedValues;
    label: string;
    standard: number;
    disabled?: boolean;
    unit: string;
}) => {
    const [field, meta, helpers] = useField<number>(props.name);
    const tooltip = helpText[props.name];

    const displayStandard = props.standard
        ? convertSpeed(props.standard, props.unit)
        : null;

    const displayValue = field.value
        ? convertSpeed(field.value, props.unit)
        : '';

    const isCustomValue =
        field.value !== null &&
        String(field.value) !== '' &&
        Number(field.value) !== props.standard;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;

        if (inputValue === '') {
            helpers.setValue(null);
            return;
        }

        // Backend only handles kts, so we need to convert formik values back if in mph
        const displayValue = parseInt(inputValue);
        const ktsValue =
            props.unit === 'mph'
                ? Math.round(displayValue / KTS_TO_MPH)
                : displayValue;

        helpers.setValue(ktsValue);
    };
    return (
        <TableRow>
            <SpeedLabelCell>
                <Stack direction="row">
                    <Typography fontWeight={700}>{props.label}</Typography>
                    <Typography pl={0.5}> - {tooltip}</Typography>
                </Stack>
            </SpeedLabelCell>
            <StdCell align="center">
                {isCustomValue && displayStandard ? (
                    <Button
                        variant="outlined"
                        small
                        onClick={() => helpers.setValue(props.standard)}
                    >
                        Use {displayStandard}
                    </Button>
                ) : (
                    <Typography>
                        {props.disabled ? '--' : (displayStandard ?? '--')}
                    </Typography>
                )}
            </StdCell>
            {props.disabled ? (
                <StdCell>
                    <Typography color={FFTheme.blue} fontWeight="bold">
                        --
                    </Typography>
                </StdCell>
            ) : (
                <EditCell>
                    <ForeFlightTextField
                        variant="standard"
                        size="small"
                        placeholder={String(displayStandard || '--')}
                        name={field.name}
                        value={displayValue}
                        onChange={handleChange}
                        onBlur={field.onBlur}
                        helperText={
                            Boolean(meta.touched && meta.error) && meta.error
                        }
                        error={Boolean(meta.touched && meta.error)}
                    />
                </EditCell>
            )}
        </TableRow>
    );
};

const SpeedLabelCell = styled(TableCell)`
    width: 100%;
    font-size: 14px;
    font-weight: 700;
`;

const StdCell = styled(TableCell)`
    font-size: 14px;
    text-align: center;
    min-width: 120px;
`;

const EditCell = styled(TableCell)`
    min-width: 120px;
    font-size: 14px;
    padding-right: 0;
    input {
        text-align: end;
    }
`;

const helpText: { [speed in keyof VspeedValues]: string } = {
    vr: 'Rotation speed',
    vx: 'Best angle-of-climb speed',
    vy: 'Best rate-of-climb speed',
    vref: 'Landing reference airspeed',
    vne: 'Never-exceed speed',
    vs0: 'Stalling speed',
    vs1: 'Stalling speed',
    vsw: 'Recommended touchdown speed',
    vmxt: 'Maximum taxi ground speed',
};

export default SpeedRow;
